import difference from 'lodash/difference';
import intersection from 'lodash/intersection';

import {
  BABYBOOMER_FEET,
  BABYBOOMER_HANDS,
  CLEANING_PRODUCTS,
  CLEANING_RECURRING,
  CLEANING_SPOT,
  EYE_CARE,
  FACE_CARE_PRESTATIONS,
  FEET_NAIL_POLISH_BASES,
  FEET_PEDICURE_BASES,
  FEET_PERMANENT_NAIL_POLISH,
  FRENCH_SUPPLEMENT_FEET,
  FRENCH_SUPPLEMENT_HANDS,
  HAIR_REMOVAL_BIKINI_LINE_FULL,
  HAIR_REMOVAL_INTERGLUTEAL_CLEFT,
  HAIRCARE_PATINA,
  HAIRCARE_PATINA_BASES,
  HAIRCUT_WOMAN_TECHNICAL_PRESTATIONS_WITHOUT_PATINA,
  HANDS_GEL_NAIL_POLISH,
  HANDS_MANICURE_BASES,
  HANDS_NAIL_POLISH_BASES,
  MAN_COLOR,
  MAN_SHAMPOO,
  MANICURE,
  PEDICURE,
  WOMAN_HAIR_HIGHLIGHTING,
  WOMAN_SHAMPOO,
  WOMAN_SHAMPOO_BASES,
  WOMAN_STREAKS,
} from '../constants/prestations';
import { CATEGORIES } from '../constants/universes';

type PrestationsRulesParams = {
  prestationsWithDependencies: string[];
  dependency: string;
  coveredBy: string[];
};

type PrestationsPrerequisiteParams = {
  prestationsWithDependencies: string[];
  dependencies: string[];
};

/**
 * This function allows to add prestationsWithDependencies
 * only if there is at least one prestation inside
 * dependencies that is included in the prestations of your basket
 *
 * Exemple:
 * dependencies = [
 *    "radiance_care",
 *    "purifying_care",
 *    "face_care_scrub",
 *    "face_care_anti_ageing",
 *    "face_care_moisturising"
 * ]
 * prestationsWithDependencies= "eye_care"
 * prestations = ["eye_care"]
 *
 * You will not be able to add the "eye_care" prestation if there is not at least
 * one prestation of dependencies in the basket
 */
const addOnlyIfAtLeastOneOtherPrestation =
  ({ prestationsWithDependencies, dependencies }: PrestationsPrerequisiteParams) =>
  (prestations: string[]) => {
    if (
      intersection(prestations, prestationsWithDependencies).length > 0 &&
      intersection(prestations, dependencies).length === 0
    ) {
      return difference(prestations, prestationsWithDependencies);
    }
    return prestations;
  };

/**
 * A package is one opaque ref for the front, so a rule looking for one of its parts never
 * finds it. coveredBy therefore lists the packages alongside the unitary refs.
 */
const isAlreadyCovered = (prestations: string[], coveredBy: string[]) =>
  prestations.some((ref) => coveredBy.includes(ref));

const addSuggestedPrestation =
  ({ prestationsWithDependencies, dependency, coveredBy }: PrestationsRulesParams) =>
  (prestations: string[], newPrestation?: string) => {
    if (!newPrestation || !prestationsWithDependencies.includes(newPrestation)) return prestations;
    if (isAlreadyCovered(prestations, coveredBy)) return prestations;

    return prestations.includes(dependency) ? prestations : prestations.concat(dependency);
  };

/**
 * Adds `dependency` when the basket holds one of the prestationsWithDependencies and nothing
 * in it covers `dependency`. The user CANNOT remove what it adds.
 *
 * "french_supplement_hands" in a basket with no hands polish -> "hands_gel_nail_polish" added.
 */
const addRequiredPrestation =
  ({ prestationsWithDependencies, dependency, coveredBy }: PrestationsRulesParams) =>
  (prestations: string[]) => {
    if (!prestations.some((ref) => prestationsWithDependencies.includes(ref))) return prestations;
    if (isAlreadyCovered(prestations, coveredBy)) return prestations;

    return prestations.includes(dependency) ? prestations : prestations.concat(dependency);
  };

export default {
  [CATEGORIES.WOMAN]: [
    addRequiredPrestation({
      prestationsWithDependencies: HAIRCUT_WOMAN_TECHNICAL_PRESTATIONS_WITHOUT_PATINA,
      dependency: WOMAN_SHAMPOO,
      coveredBy: WOMAN_SHAMPOO_BASES,
    }),
    addSuggestedPrestation({
      prestationsWithDependencies: [WOMAN_HAIR_HIGHLIGHTING, WOMAN_STREAKS],
      dependency: HAIRCARE_PATINA,
      coveredBy: HAIRCARE_PATINA_BASES,
    }),
  ],
  [CATEGORIES.MAN]: [
    addSuggestedPrestation({
      prestationsWithDependencies: [MAN_COLOR],
      dependency: MAN_SHAMPOO,
      coveredBy: [MAN_SHAMPOO],
    }),
  ],
  [CATEGORIES.BODYCARE]: [
    addRequiredPrestation({
      prestationsWithDependencies: [FRENCH_SUPPLEMENT_HANDS],
      dependency: HANDS_GEL_NAIL_POLISH,
      coveredBy: HANDS_NAIL_POLISH_BASES,
    }),
    addRequiredPrestation({
      prestationsWithDependencies: [BABYBOOMER_HANDS],
      dependency: HANDS_GEL_NAIL_POLISH,
      coveredBy: HANDS_NAIL_POLISH_BASES,
    }),
    addSuggestedPrestation({
      prestationsWithDependencies: [HANDS_GEL_NAIL_POLISH],
      dependency: MANICURE,
      coveredBy: HANDS_MANICURE_BASES,
    }),
    addRequiredPrestation({
      prestationsWithDependencies: [FRENCH_SUPPLEMENT_FEET],
      dependency: FEET_PERMANENT_NAIL_POLISH,
      coveredBy: FEET_NAIL_POLISH_BASES,
    }),
    addRequiredPrestation({
      prestationsWithDependencies: [BABYBOOMER_FEET],
      dependency: FEET_PERMANENT_NAIL_POLISH,
      coveredBy: FEET_NAIL_POLISH_BASES,
    }),
    addSuggestedPrestation({
      prestationsWithDependencies: [FEET_PERMANENT_NAIL_POLISH],
      dependency: PEDICURE,
      coveredBy: FEET_PEDICURE_BASES,
    }),
  ],
  [CATEGORIES.MASSAGE]: [
    addOnlyIfAtLeastOneOtherPrestation({
      prestationsWithDependencies: [EYE_CARE],
      dependencies: FACE_CARE_PRESTATIONS,
    }),
  ],
  [CATEGORIES.MASSAGE_WOMAN]: [
    addOnlyIfAtLeastOneOtherPrestation({
      prestationsWithDependencies: [EYE_CARE],
      dependencies: FACE_CARE_PRESTATIONS,
    }),
  ],
  [CATEGORIES.HAIR_REMOVAL]: [
    addSuggestedPrestation({
      prestationsWithDependencies: [HAIR_REMOVAL_BIKINI_LINE_FULL],
      dependency: HAIR_REMOVAL_INTERGLUTEAL_CLEFT,
      coveredBy: [HAIR_REMOVAL_INTERGLUTEAL_CLEFT],
    }),
  ],
  // TODO : DELETE_WHEN_PRO_USE_PRESTATIONS_WITH_OPTIONS_ON_BOOKING
  [CATEGORIES.RECURRING]: [
    addOnlyIfAtLeastOneOtherPrestation({
      prestationsWithDependencies: [CLEANING_PRODUCTS],
      dependencies: [CLEANING_RECURRING, CLEANING_SPOT],
    }),
  ],
};
